/** @typedef {'cued' | 'prose'} TranscriptLayout */
/** @typedef {'txt' | 'docx' | 'csv'} TranscriptFileFormat */
/** @typedef {'smpte' | 'ms' | 'seconds'} TranscriptTcFormat */
/** @typedef {'add' | 'subtract'} TranscriptTcOffsetType */

/**
 * @typedef {'index' | 'start' | 'end' | 'speakers' | 'text' | 'original'
 *   | 'tags' | 'notes' | 'notesCheck' | 'reply' | 'replyCheck' | 'approved' | 'forced'} TranscriptFieldId
 */

/** @typedef {0 | 1 | 2} TranscriptBreakAfter */
/** @typedef {'none' | 'square' | 'round' | 'curly'} TranscriptWrap */

/**
 * @typedef {Object} TranscriptFieldConfig
 * @property {TranscriptFieldId} id
 * @property {boolean} enabled
 * @property {TranscriptBreakAfter} breakAfter Number of line breaks after the field (0 = inline).
 * @property {boolean} bold
 * @property {boolean} italic
 * @property {boolean} showLabel Prefix the value with its label (e.g. "Notes: ").
 * @property {TranscriptWrap} wrap
 */

/**
 * @typedef {Object} TranscriptExportOptions
 * @property {TranscriptLayout} layout
 * @property {TranscriptFileFormat} fileFormat
 * @property {TranscriptTcFormat} tcFormat
 * @property {boolean} exportMetadata
 * @property {boolean} preserveLineBreaks
 * @property {boolean} autoGroupByTc
 * @property {number} duration
 * @property {string} tcOffset
 * @property {TranscriptTcOffsetType} tcOffsetType
 * @property {TranscriptFieldConfig[]} fieldLayout
 */

/** @type {TranscriptFieldId[]} */
export const TRANSCRIPT_FIELD_IDS = [
    'index',
    'start',
    'end',
    'speakers',
    'text',
    'original',
    'tags',
    'notes',
    'notesCheck',
    'reply',
    'replyCheck',
    'approved',
    'forced',
];

/** Fields allowed in prose layout (besides project metadata). */
export const PROSE_FIELD_IDS = new Set(['index', 'start', 'end', 'speakers', 'text']);

/** Fields whose value is prefixed with a label (e.g. "Notes: ..."). */
export const LABELED_FIELD_IDS = new Set([
    'original',
    'tags',
    'notes',
    'notesCheck',
    'reply',
    'replyCheck',
    'approved',
    'forced',
]);

/** @type {TranscriptWrap[]} */
export const TRANSCRIPT_WRAP_OPTIONS = ['none', 'square', 'round', 'curly'];

/**
 * @param {unknown} value
 * @param {TranscriptBreakAfter} fallback
 * @returns {TranscriptBreakAfter}
 */
export function normalizeBreakAfter(value, fallback = 1) {
    if (value === true) return 1;
    if (value === false) return 0;
    if (value === 0 || value === 1 || value === 2) return value;
    return fallback;
}

/**
 * @param {unknown} value
 * @returns {TranscriptWrap}
 */
export function normalizeWrap(value) {
    return TRANSCRIPT_WRAP_OPTIONS.includes(/** @type {TranscriptWrap} */ (value))
        ? /** @type {TranscriptWrap} */ (value)
        : 'none';
}

/** @returns {TranscriptFieldConfig[]} */
export function getDefaultFieldLayout() {
    /** @type {Array<Omit<TranscriptFieldConfig, 'showLabel' | 'wrap'>>} */
    const fields = [
        { id: 'index', enabled: true, breakAfter: 1, bold: true, italic: false },
        { id: 'start', enabled: true, breakAfter: 0, bold: false, italic: true },
        { id: 'end', enabled: true, breakAfter: 1, bold: false, italic: true },
        { id: 'speakers', enabled: true, breakAfter: 0, bold: true, italic: false },
        { id: 'text', enabled: true, breakAfter: 1, bold: false, italic: false },
        { id: 'original', enabled: false, breakAfter: 1, bold: false, italic: false },
        { id: 'tags', enabled: false, breakAfter: 1, bold: false, italic: false },
        { id: 'notes', enabled: false, breakAfter: 1, bold: true, italic: false },
        { id: 'notesCheck', enabled: false, breakAfter: 1, bold: false, italic: false },
        { id: 'reply', enabled: false, breakAfter: 1, bold: false, italic: false },
        { id: 'replyCheck', enabled: false, breakAfter: 1, bold: false, italic: false },
        { id: 'approved', enabled: false, breakAfter: 1, bold: false, italic: false },
        { id: 'forced', enabled: false, breakAfter: 1, bold: false, italic: false },
    ];
    return fields.map((f) => ({ ...f, showLabel: true, wrap: 'none' }));
}

/**
 * @param {TranscriptFieldId} id
 * @returns {TranscriptFieldConfig}
 */
function defaultFieldConfig(id) {
    return getDefaultFieldLayout().find((f) => f.id === id) || {
        id,
        enabled: id === 'text',
        breakAfter: 1,
        bold: false,
        italic: false,
        showLabel: true,
        wrap: 'none',
    };
}

/**
 * Validate/merge a field layout against defaults. Forces text.enabled = true.
 * @param {unknown} raw
 * @returns {TranscriptFieldConfig[]}
 */
export function normalizeFieldLayout(raw) {
    const defaults = getDefaultFieldLayout();
    const byId = new Map(defaults.map((f) => [f.id, { ...f }]));

    if (Array.isArray(raw)) {
        /** @type {TranscriptFieldConfig[]} */
        const ordered = [];
        const seen = new Set();

        for (const item of raw) {
            if (!item || typeof item !== 'object') continue;
            const id = /** @type {TranscriptFieldId} */ (item.id);
            if (!byId.has(id) || seen.has(id)) continue;
            seen.add(id);
            const base = byId.get(id);
            ordered.push({
                id,
                enabled: id === 'text' ? true : Boolean(item.enabled),
                breakAfter: normalizeBreakAfter(item.breakAfter, base.breakAfter),
                bold: item.bold !== undefined ? Boolean(item.bold) : base.bold,
                italic: item.italic !== undefined ? Boolean(item.italic) : base.italic,
                showLabel: item.showLabel !== undefined ? Boolean(item.showLabel) : base.showLabel,
                wrap: normalizeWrap(item.wrap),
            });
        }

        for (const field of defaults) {
            if (!seen.has(field.id)) {
                ordered.push({
                    ...field,
                    enabled: field.id === 'text' ? true : false,
                });
            }
        }
        return ordered;
    }

    return defaults.map((f) => ({ ...f }));
}

/**
 * Build field layout from legacy boolean export flags.
 * @param {Record<string, unknown>} config
 * @returns {TranscriptFieldConfig[]}
 */
export function fieldLayoutFromLegacyBooleans(config = {}) {
    const layout = getDefaultFieldLayout().map((f) => ({ ...f }));
    const setEnabled = (id, value) => {
        const field = layout.find((f) => f.id === id);
        if (field && id !== 'text') field.enabled = Boolean(value);
    };

    if (config.exportIncode !== undefined) setEnabled('start', config.exportIncode);
    if (config.exportOutcode !== undefined) setEnabled('end', config.exportOutcode);
    if (config.exportSpeakers !== undefined) setEnabled('speakers', config.exportSpeakers);
    if (config.exportPrimaryLang !== undefined) setEnabled('original', config.exportPrimaryLang);
    if (config.exportTags !== undefined) setEnabled('tags', config.exportTags);
    if (config.exportNotes !== undefined) setEnabled('notes', config.exportNotes);
    if (config.exportNotesCheck !== undefined) setEnabled('notesCheck', config.exportNotesCheck);
    if (config.exportReply !== undefined) setEnabled('reply', config.exportReply);
    if (config.exportReplyCheck !== undefined) setEnabled('replyCheck', config.exportReplyCheck);
    if (config.exportApproved !== undefined) setEnabled('approved', config.exportApproved);
    if (config.exportForced !== undefined) setEnabled('forced', config.exportForced);

    // Older cued exports always showed an index
    const indexField = layout.find((f) => f.id === 'index');
    if (indexField && config.exportIndex !== undefined) {
        indexField.enabled = Boolean(config.exportIndex);
    }

    return layout;
}

/**
 * @param {TranscriptFieldConfig[]} fieldLayout
 * @param {TranscriptFieldId} id
 * @returns {boolean}
 */
export function isFieldEnabled(fieldLayout, id) {
    const field = fieldLayout.find((f) => f.id === id);
    return field ? Boolean(field.enabled) : id === 'text';
}

/** @returns {TranscriptExportOptions} */
export function getDefaultTranscriptExportOptions() {
    return {
        layout: 'cued',
        fileFormat: 'txt',
        tcFormat: 'smpte',
        exportMetadata: true,
        preserveLineBreaks: false,
        autoGroupByTc: true,
        duration: 2,
        tcOffset: '00:00:00:00',
        tcOffsetType: 'add',
        fieldLayout: getDefaultFieldLayout(),
    };
}

/**
 * Map legacy paragraph/subtitle presets to layout modes.
 * @param {string | undefined} preset
 * @returns {TranscriptLayout | undefined}
 */
export function layoutFromLegacyPreset(preset) {
    if (preset === 'paragraph') return 'prose';
    if (preset === 'subtitle') return 'cued';
    return undefined;
}

/**
 * Normalize saved preset config (including legacy keys) into current options.
 * @param {Record<string, unknown>} config
 * @param {Partial<TranscriptExportOptions>} [base]
 * @returns {TranscriptExportOptions}
 */
export function normalizeTranscriptExportConfig(config = {}, base = {}) {
    const defaults = { ...getDefaultTranscriptExportOptions(), ...base };
    const layout =
        (config.layout === 'cued' || config.layout === 'prose'
            ? config.layout
            : layoutFromLegacyPreset(/** @type {string | undefined} */ (config.preset))) ||
        defaults.layout;

    const fieldLayout = Array.isArray(config.fieldLayout)
        ? normalizeFieldLayout(config.fieldLayout)
        : fieldLayoutFromLegacyBooleans(config);

    // Ensure text is always on
    const textField = fieldLayout.find((f) => f.id === 'text');
    if (textField) textField.enabled = true;

    return {
        layout,
        fileFormat:
            config.fileFormat === 'txt' || config.fileFormat === 'docx' || config.fileFormat === 'csv'
                ? config.fileFormat
                : defaults.fileFormat,
        tcFormat:
            config.tcFormat === 'smpte' || config.tcFormat === 'ms' || config.tcFormat === 'seconds'
                ? config.tcFormat
                : defaults.tcFormat,
        exportMetadata: config.exportMetadata !== undefined ? Boolean(config.exportMetadata) : defaults.exportMetadata,
        preserveLineBreaks:
            config.preserveLineBreaks !== undefined ? Boolean(config.preserveLineBreaks) : defaults.preserveLineBreaks,
        autoGroupByTc: config.autoGroupByTc !== undefined ? Boolean(config.autoGroupByTc) : defaults.autoGroupByTc,
        duration: typeof config.duration === 'number' ? config.duration : defaults.duration,
        tcOffset: typeof config.tcOffset === 'string' ? config.tcOffset : defaults.tcOffset,
        tcOffsetType: config.tcOffsetType === 'subtract' ? 'subtract' : defaults.tcOffsetType,
        fieldLayout,
    };
}

/**
 * Serializable preset config (current shape only).
 * @param {TranscriptExportOptions} options
 * @returns {Record<string, unknown>}
 */
export function toPresetConfig(options) {
    return {
        layout: options.layout,
        fileFormat: options.fileFormat,
        tcFormat: options.tcFormat,
        exportMetadata: options.exportMetadata,
        preserveLineBreaks: options.preserveLineBreaks,
        autoGroupByTc: options.autoGroupByTc,
        duration: options.duration,
        tcOffset: options.tcOffset,
        tcOffsetType: options.tcOffsetType,
        fieldLayout: normalizeFieldLayout(options.fieldLayout),
    };
}

export const TRANSCRIPT_EXPORT_DEFAULTS_KEY = 'cc-transcript-export-defaults';

/**
 * Last-used export settings from localStorage. Omits tcOffset (project-specific).
 * @param {Partial<TranscriptExportOptions>} [base]
 * @returns {TranscriptExportOptions}
 */
export function loadSavedTranscriptExportOptions(base = {}) {
    try {
        const raw = JSON.parse(localStorage.getItem(TRANSCRIPT_EXPORT_DEFAULTS_KEY) || 'null');
        if (!raw || typeof raw !== 'object') {
            return { ...getDefaultTranscriptExportOptions(), ...base };
        }
        const { tcOffset: _ignored, ...rest } = raw;
        return normalizeTranscriptExportConfig(rest, base);
    } catch {
        return { ...getDefaultTranscriptExportOptions(), ...base };
    }
}

/**
 * Persist last-used export settings. Omits tcOffset (project-specific).
 * @param {TranscriptExportOptions} options
 */
export function saveTranscriptExportOptions(options) {
    try {
        const { tcOffset: _ignored, ...rest } = toPresetConfig(options);
        localStorage.setItem(TRANSCRIPT_EXPORT_DEFAULTS_KEY, JSON.stringify(rest));
    } catch (error) {
        console.warn('Failed to save transcript export defaults:', error);
    }
}

/**
 * Whether per-event metadata includes (beyond speakers/times/text) are active for the current layout.
 * @param {TranscriptExportOptions} options
 * @returns {boolean}
 */
export function isCuedMetadataEnabled(options) {
    return options.layout === 'cued' || options.fileFormat === 'csv';
}

/**
 * Convenience: map field layout to legacy-style include flags for builders that still need them.
 * @param {TranscriptFieldConfig[]} fieldLayout
 */
export function flagsFromFieldLayout(fieldLayout) {
    return {
        exportIncode: isFieldEnabled(fieldLayout, 'start'),
        exportOutcode: isFieldEnabled(fieldLayout, 'end'),
        exportSpeakers: isFieldEnabled(fieldLayout, 'speakers'),
        exportPrimaryLang: isFieldEnabled(fieldLayout, 'original'),
        exportTags: isFieldEnabled(fieldLayout, 'tags'),
        exportNotes: isFieldEnabled(fieldLayout, 'notes'),
        exportNotesCheck: isFieldEnabled(fieldLayout, 'notesCheck'),
        exportReply: isFieldEnabled(fieldLayout, 'reply'),
        exportReplyCheck: isFieldEnabled(fieldLayout, 'replyCheck'),
        exportApproved: isFieldEnabled(fieldLayout, 'approved'),
        exportForced: isFieldEnabled(fieldLayout, 'forced'),
        exportIndex: isFieldEnabled(fieldLayout, 'index'),
    };
}

export { defaultFieldConfig };
